import { useSearchParams, useNavigate} from "react-router-dom"
import Fuse from "fuse.js"
import "../styles/Studies.css"
import React, { useState, useEffect, useRef, useMemo } from "react"
import { supabase } from "../supabase"
import magnifying from "../assets/magnifying-glass-18.png"
import desc from "../assets/desc.png"
import { TagSort, Tag } from "./TagSort.js"
import CustomSlide from "./CustomSlide.js"

// import RangeSlider from "../extraComponents/RangeSlider.js"

// antd
import { Slider } from "antd";


function convertDate(dateStr) {
    const date = new Date(dateStr);

    // short month, full year format
    const options = { year: 'numeric', month: 'short' }; 
    // format date
    const formattedDate = date.toLocaleDateString('en-US', options);

    return formattedDate;
}

function formatFullDate(dateStr) {
    if (!dateStr) return "";
    
    // Handle timezone issues by parsing as UTC
    const date = new Date(dateStr + 'T00:00:00Z'); // Force UTC interpretation
    
    const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        timeZone: 'UTC' // Ensure UTC timezone
    };
    
    return date.toLocaleDateString('en-US', options);
}

function compareDates(dateStr, year1, year2) {
    const date = new Date(dateStr)
    const Date1 = new Date(`${year1}-01-01`)
    const Date2 = new Date(`${year2 + 1}-01-01`)

    return date >= Date1 && date <= Date2
}

function convertDuration(dur) {
    const categories = [[4, "Week", 1], [51, "Month", 4], [10000, "Year", 52]]
    for (let i = 0; i < categories.length; i++) {
        const cat = categories[i]
        if (dur <= cat[0]) {
            const value = Math.floor(dur/cat[2])
            const unit = value === 1 ? cat[1] : `${cat[1]}s`
            return value.toString() + " " + unit
        }
    }
}

const Clinical = ({narrow, setNarrow, open, isAdmin}) => {
    
    // Keep tag UI components, but remove tag fetching/usage here.

    
    const nav = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState(searchParams.get("q") || "")
    const appliedSearch = useMemo(() => searchParams.get("q") || "", [searchParams])

    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === "undefined") return false
        return window.matchMedia("(max-width: 800px)").matches
    })

    useEffect(() => {
        if (typeof window === "undefined") return
        const mq = window.matchMedia("(max-width: 800px)")
        const handler = (e) => setIsMobile(e.matches)
        // Safari support
        if (mq.addEventListener) mq.addEventListener("change", handler)
        else mq.addListener(handler)

        // initialize
        setIsMobile(mq.matches)

        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", handler)
            else mq.removeListener(handler)
        }
    }, [])

    // Keep the input in sync when navigating back/forward (q param changes)
    useEffect(() => {
        setSearch(appliedSearch)
    }, [appliedSearch])

    const [studies, setStudies] = useState([])
    
    const[openDesc, setOpenDesc] = useState(-1)

    // Favorites state
    const [favoriteStudies, setFavoriteStudies] = useState([])
    const [userId, setUserId] = useState(null)
    const favoriteStudiesRef = useRef([])
    
    // Keep ref in sync with state
    useEffect(() => {
        favoriteStudiesRef.current = favoriteStudies
    }, [favoriteStudies])

    

    // Tag-based scoring removed; sorting is entirely search-based

    // Duration/sample helpers and state (moved earlier to prevent reference errors)
    function getDuration() {
        const origin = searchParams.get("duration")?.split(",")
        if (origin != null) {
            return [Number(origin[0]), Number(origin[1])]
        }
        return null
    }

    const [currentDuration, setCurrentDuration] = useState(getDuration() || [0, 120])

    // SAMPLE SIZE SLIDER COMMENTED OUT
    // function getSample() {
    //     const origin = searchParams.get("sample")?.split(",")
    //     if (origin != null) {
    //         return [Number(origin[0]), Number(origin[1])]
    //     }
    //     return null
    // }

    // const [currentSample, setSample] = useState(getSample() || [0, 1000])
    // const sampleExceed = useMemo(() => searchParams.get("sampleExceed") === "true", [searchParams]);

    // Favorites and sorting state
    const [favoritesOnly, setFavoritesOnly] = useState(searchParams.get("favoritesOnly") === "true")
    const [sortNewest, setSortNewest] = useState(searchParams.get("sortNewest") === "true")

    function getTimes() {
        const origin = searchParams.get("times")?.split(",")
        if (origin != null) {
                return [Number(origin[0]), Number(origin[1])]
        }
        return null
    }

    // let timelineOnlyChange = false
    const [preTimeline, setPreTimeline] = useState({}) // timeline study amounts data (done before applying time filters)
    const currentYear = new Date().getFullYear()
    const [selectedTimes, setSelectedTimes] = useState(getTimes() || [1800, currentYear])
    const [finalizedTimes, setFinalizedTimes] = useState(getTimes() || [1800, currentYear])

    function applyTimes() {
        const timeparam = new URLSearchParams(searchParams.toString());

        timeparam.set(
            "times",
            `${selectedTimes[0]},${selectedTimes[1]}`
        )
        setSearchParams(timeparam);

        setFinalizedTimes(selectedTimes)
    }

    function preTimelineSort(data) {
        let returner = {}

        for (let i = 0; i < data.length; i++) {

            const current = data[i]
            const date = current.date
            const year = new Date(date).getFullYear()

            if (returner[year]) {
                returner[year]++
            }
            else {
                returner[year] = 1
            }
        }

        return returner
    }

    // Fetch user's favorite studies on mount
    useEffect(() => {
        const fetchUserFavorites = async () => {
            const { data: { user } } = await supabase.auth.getUser()
            
            if (user) {
                setUserId(user.id)
                
                const { data, error } = await supabase
                    .from('profiles')
                    .select('favourite_studies')
                    .eq('id', user.id)
                    .single()
                
                if (data && !error) {
                    setFavoriteStudies(data.favourite_studies || [])
                }
            }
        }
        
        fetchUserFavorites()
    }, [])

    // Toggle favorite status
    const toggleFavorite = async (studyId, e) => {
        e.stopPropagation() // Prevent card click navigation
        
        if (!userId) {
            console.log('User must be logged in to favorite studies')
            return
        }

        const isFavorited = favoriteStudies.includes(studyId)
        const updatedFavorites = isFavorited
            ? favoriteStudies.filter(id => id !== studyId)
            : [...favoriteStudies, studyId]
        
        // Get current study before optimistic update
        const currentStudy = studies.find(s => s.id === studyId)
        const currentFavCount = currentStudy?.Favourites || 0
        
        console.log('Toggle favorite for study:', studyId)
        console.log('Current Favourites count:', currentFavCount)
        console.log('isFavorited:', isFavorited)
        
        // Optimistically update UI
        setFavoriteStudies(updatedFavorites)
        
        // Optimistically update the Favourites count in the studies list
        setStudies(prevStudies => 
            prevStudies.map(study => 
                study.id === studyId 
                    ? { ...study, Favourites: currentFavCount + (isFavorited ? -1 : 1) }
                    : study
            )
        )
        
        // Update user's favorites in database
        const { error: profileError } = await supabase
            .from('profiles')
            .update({ favourite_studies: updatedFavorites })
            .eq('id', userId)
        
        if (profileError) {
            console.error('Profile update error:', profileError)
        } else {
            console.log('Profile updated successfully')
        }
        
        // Update the study's Favourites count - use the original count we captured
        const newFavCount = Math.max(0, currentFavCount + (isFavorited ? -1 : 1))
        
        console.log('Updating study Favourites to:', newFavCount)
        
        const { data: updateData, error: studyError } = await supabase
            .from('study_entries')
            .update({ Favourites: newFavCount })
            .eq('id', studyId)
            .select()
        
        console.log('Study update response:', { data: updateData, error: studyError })
        
        // Verify the update by fetching the study
        const { data: verifyData, error: verifyError } = await supabase
            .from('study_entries')
            .select('id, Title, Favourites')
            .eq('id', studyId)
            .single()
        
        console.log('Verification fetch after update:', { data: verifyData, error: verifyError })
        
        if (profileError || studyError) {
            console.error('Error updating favorites:', { profileError, studyError })
            // Revert on error
            setFavoriteStudies(favoriteStudies)
            setStudies(prevStudies => 
                prevStudies.map(study => 
                    study.id === studyId 
                        ? { ...study, Favourites: currentFavCount }
                        : study
                )
            )
        } else {
            console.log('✓ Successfully updated favorites. New count:', newFavCount)
            console.log('✓ Database now shows:', verifyData?.Favourites)
        }
    }

    // Toggle favorites filter
    const toggleFavoritesOnly = () => {
        const newValue = !favoritesOnly
        setFavoritesOnly(newValue)
        
        const param = new URLSearchParams(searchParams.toString())
        if (newValue) {
            param.set("favoritesOnly", "true")
        } else {
            param.delete("favoritesOnly")
        }
        setSearchParams(param)
    }

    // Toggle newest sort
    const toggleSortNewest = () => {
        const newValue = !sortNewest
        setSortNewest(newValue)
        
        const param = new URLSearchParams(searchParams.toString())
        if (newValue) {
            param.set("sortNewest", "true")
        } else {
            param.delete("sortNewest")
        }
        setSearchParams(param)
    }

    // retreiving new data whenever parameters are changed (like given dates, duration, etc)
    const searchParamsString = searchParams.toString();
    useEffect(() => {
        // SAMPLE SIZE FILTERING COMMENTED OUT
        // const searchSample = sampleExceed ? [1001, 1000000000] : currentSample
        

        const fetchStudies = async () => {
            const { data, error } = await supabase
            .from('study_entries')
            .select('*')
            // .gte("sample_size", searchSample[0])
            // .lte("sample_size", searchSample[1])
            .gte("Duration", Math.round(currentDuration[0] * 4.34524))
            .lte("Duration", Math.round(currentDuration[1] * 4.34524))

            let output = data

            const searchfuse = new Fuse(data, {
                keys: [
                    { name: "Title", weight: 1 },
                    { name: "mini_sum", weight: 1.2 }
                ],
                threshold: 0.37,
                distance: 120,
                minMatchCharLength: 2,
                shouldSort: true,
                includeScore: true,
                includeMatches: true,
                ignoreLocation: true
            })

            const query = (appliedSearch || "").trim()
            if (query !== "") {
                const results = searchfuse.search(query)
                output = results.map(r => r.item)
            }

            // Apply newest sort
            if (sortNewest) {
                output = output.sort((a, b) => new Date(b.date) - new Date(a.date))
            }

            setPreTimeline(preTimelineSort(output))
            //timeline filter after setting the data counts before time filters
            

          if (error) {
            console.error('Error fetching studies:', error)
          } else {
            // Apply favorites filter after setting studies - use ref to avoid refetch on favorite changes
            if (favoritesOnly && userId) {
                setStudies(output.filter(study => favoriteStudiesRef.current.includes(study.id)))
            } else {
                setStudies(output)
            }
          }
        }
    
                fetchStudies()
            }, [searchParamsString, currentDuration, favoritesOnly, sortNewest, userId, appliedSearch]) // REMOVED: currentSample, sampleExceed

    function applySearch(param) {
        const updatedParams = new URLSearchParams(searchParams.toString());
        if (search.trim() !== "") {
            updatedParams.set("q", search);
        } else {
            updatedParams.delete("q"); // optional: clear q if empty
        }
        setSearchParams(updatedParams);
        
    }

    function clearSearch() {
        setSearch("")
        const updatedParams = new URLSearchParams(searchParams.toString())
        updatedParams.delete("q")
        setSearchParams(updatedParams)
    }

    function handleDesc(i) {

        let newopen = openDesc === i ? -1 : i
        
        setOpenDesc(newopen)
    }


    // --------------- PARENT TAG LOGIC --------------------
    // selected tags is just getting all the tags from the search param
    const selectedTags = useMemo(() => {
        const getTags = () => {
            const origin = searchParams.get("tags")?.split(",")
            if (origin != null) {
                return origin
                .map(w => w
                .split(" ")
                .map(nw => nw.charAt(0).toUpperCase() + nw.slice(1))
                .join(" ")) 
            }
            return null
        }
        return getTags() || []
    }, [searchParams])

    

    function applyDuration() {
        const durparam = new URLSearchParams(searchParams.toString());

        durparam.set(
            "duration",
            `${currentDuration[0]},${currentDuration[1]}`
        )
        setSearchParams(durparam);
    }

    // SAMPLE SIZE FUNCTIONS COMMENTED OUT
    // function applySample() {
    //     const sampleparam = new URLSearchParams(searchParams.toString());

    //     sampleparam.set(
    //         "sample",
    //         `${currentSample[0]},${currentSample[1]}`
    //     )
    //     setSearchParams(sampleparam);
    // }

    // function setExceed(newVal) {
    //     const param = new URLSearchParams(searchParams.toString());

    //     param.set(
    //         "sampleExceed",
    //         newVal
    //     )
    //     setSearchParams(param);
    // }

    return (
        
        <div className="mainstudies">

            {!isMobile && <Sort inline={false} open={open}
            selectedTags={selectedTags}
            changeDuration={setCurrentDuration} currentDuration={currentDuration} applyDuration={applyDuration}
            timelineData={preTimeline} setSelectedTimes={setSelectedTimes} applyTimes={applyTimes} selectedTimes={selectedTimes} narrow={false}
            favoritesOnly={favoritesOnly} toggleFavoritesOnly={toggleFavoritesOnly}
            sortNewest={sortNewest} toggleSortNewest={toggleSortNewest}
            userId={userId} />}
            <div className="rightstudies">
                {/* <h1>All Studies</h1> */}
                <div className="search">

                    <div className="searchbar">
                        <img src={magnifying} onClick={applySearch} loading="lazy" alt="search" />

                        <input 
                        value={search}
                        className="searchelem" 
                        placeholder="Search Anything" 
                        onChange={(e) => {
                            if (e.target.value !== null) {
                                setSearch(e.target.value)
                            }
                        }   
                        } 
                        onKeyDown={
                            (e) => {
                                if (e.key === "Enter") {
                                    applySearch()
                                }
                            }
                        } />
                        {search.trim() !== "" && (
                            <button className="clearSearch" onClick={clearSearch} aria-label="Clear search">×</button>
                        )}
                    </div>
                    
                </div>

                {isMobile && <Sort inline={true} selectedTags={selectedTags} open={open}
                changeDuration={setCurrentDuration} currentDuration={currentDuration} applyDuration={applyDuration}
                timelineData={preTimeline} setSelectedTimes={setSelectedTimes} applyTimes={applyTimes} selectedTimes={selectedTimes} narrow={true}
                favoritesOnly={favoritesOnly} toggleFavoritesOnly={toggleFavoritesOnly}
                sortNewest={sortNewest} toggleSortNewest={toggleSortNewest}
                userId={userId} />}
                <div className="content">
                    <div className="cards">
                        {
                            studies.map((info, i) => (
                                (compareDates(info.date, finalizedTimes[0], finalizedTimes[1]) && (isAdmin || info.Limited === false)) 
                                && (
                                    <div className="card" onClick={() => {
                                    nav(`/documents/${info.id}`)
                                }}>
                                <div className="spacer">
                                <div className="top1">
                                    <h1 className="title1">{info.Title}</h1>
                                    <button 
                                        className={`favorite-button ${userId === null ? "disabled" : "activated"}`}
                                        onClick={(e) => toggleFavorite(info.id, e)}
                                        aria-label={favoriteStudies.includes(info.id) ? "Remove from favorites" : "Add to favorites"}
                                    >
                                        <svg 
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill={favoriteStudies.includes(info.id) ? "#4e9bffff" : "none"}
                                            stroke={favoriteStudies.includes(info.id) ? "#0a2c55ff" : "#666"}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="tags">
                                    {
                                        info.Tags?.map((ninfo, j) => (
                                            <Tag key={j} info={ninfo} searchtag={false} />
                                        ))  
                                    }
                                </div>
                                
                                <div className="details">
                                    <div className="duration">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        <p>{convertDuration(info.Duration)}</p>
                                    </div>
                                    <div className="sample_size">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                        <p>{info.sample_size}</p>
                                    </div>
                                    <div className="date">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                        <p>{convertDate(info.date)}</p>
                                    </div>
                                    <div className="views">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                        </svg>
                                        <p>{info.Favourites || 0}</p>
                                    </div>
                                </div>
                                <div className="desc">
                                    <div className="lastedited">
                                        <div className="editeddot"></div>
                                        <div className="editedtext">
                                            {(() => {
                                                const d = info.last_edited
                                                const formatted = formatFullDate(d)
                                                return formatted ? `Last Edited ${formatted}` : "Last Edited";
                                            })()}
                                        </div>
                                    </div>
                                    <div className="expandable" onClick={(e) => {
                                        handleDesc(i)
                                        e.stopPropagation()
                                    }}>
                                        <img src={desc} loading="lazy" alt="description" />
                                        <div className="desctext">
                                            Description
                                        </div>
                                    </div>
                                </div>
                                <div className={`expanddesc ${openDesc === i ? "open1" : "closed1"}`}>
                                    {info.mini_sum}
                                </div>
                            </div>
                            
                        </div>
                                )
                                
                            ))
                        }
                        
                    </div>
                </div>
            </div>
        </div>
    
    )
}

const Sort = ({open, narrow, timelineData, setSelectedTimes, applyTimes, selectedTimes, inline, selectedTags, changeDuration, currentDuration, applyDuration, favoritesOnly, toggleFavoritesOnly, sortNewest, toggleSortNewest, userId}) => {
    
    // eslint-disable-next-line no-unused-vars
    const [activated, setActivated] = useState(["filters", "tags", "timeline"])

    return (
        <div className={`big ${inline ? "inline" : "out"}`}>
            <div className="buttons">
                {/* Filter buttons */}
                <div className="filter-buttons">
                        <button 
                        disabled={userId === null}
                            className={`filter-btn ${favoritesOnly ? "active" : ""} ${userId === null ? "disabled" : ""}`}
                            onClick={toggleFavoritesOnly}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={favoritesOnly ? "#006effff" : "none"} stroke="currentColor" strokeWidth="2">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                            Favorites Only
                        </button>
                    <button 
                        className={`filter-btn ${sortNewest ? "active" : ""}`}
                        onClick={toggleSortNewest}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 5v14M19 12l-7 7-7-7"/>
                        </svg>
                        Newest
                    </button>
                </div>
            </div>
            {Object.keys(timelineData).length > 0 &&
            <div className="timeline">
                <HistogramSlide open={open} title="Timeline" height={narrow ? 120 : 200} data={timelineData} selectedTimes={selectedTimes} setSelectedTimes={setSelectedTimes} applyTimes={applyTimes}/>
            </div>}
            
            <div className={`sliders ${activated.includes("filters") ? "" : "nodisplay"}`}>
                <CustomSlide title="Duration" min={0} max={120} first={currentDuration} setter={changeDuration} unit={"Years"} extraMax={10} dateConvert={true} apply={applyDuration}/>
                {/* SAMPLE SIZE SLIDER COMMENTED OUT */}
                {/* <CustomSlide title="Sample Size" min={0} max={1000} first={currentSample} setter={sampleedit} step={10} more={true} apply={applySample} exceed={{'sample': sampleExceed, "set": setExceed}}/> */}
            </div>
            <TagSort />
        </div>
    )
}





const HistogramSlide = ({ open, height, title, data, setSelectedTimes, applyTimes, selectedTimes }) => {
    const [prevDataRange, setPrevDataRange] = useState(null)

    useEffect(() => {
        // Histogram data initialization
    }, []) 

    const keys = Object.keys(data).sort((a, b) => Number(a) - Number(b))
    const maxFound = Number(keys[keys.length - 1])

    // Slider range is based on the latest study year, and always spans 20 years.
    // If there are fewer than 20 years of studies, we still expand left to (maxYear - 20)
    // so the histogram always shows a consistent 20-year window.
    const maxYear = Number.isFinite(maxFound) ? maxFound : new Date().getFullYear()
    const minYear = maxYear - 20
    const min = minYear
    const max = maxYear

    // Reset timeline selection when data range changes (e.g., when other filters change)
    useEffect(() => {
        const currentRange = `${min}-${max}`
        if (prevDataRange && prevDataRange !== currentRange) {
            // Data range changed, reset selection to full range
            setSelectedTimes([min, max])
        }
        setPrevDataRange(currentRange)
    }, [min, max, setSelectedTimes, prevDataRange])

    // Keep selection within the computed range (does not write URL until Apply).
    useEffect(() => {
        if (!Array.isArray(selectedTimes) || selectedTimes.length !== 2) return
        const next = [
            Math.max(min, Math.min(max, Number(selectedTimes[0]))),
            Math.max(min, Math.min(max, Number(selectedTimes[1])))
        ]
        if (next[0] > next[1]) {
            next[0] = min
            next[1] = max
        }
        if (next[0] !== selectedTimes[0] || next[1] !== selectedTimes[1]) {
            setSelectedTimes(next)
        }
    }, [min, max, selectedTimes, setSelectedTimes])
    
    const [histogramData, setHistogramData] = useState([])
    useEffect(() => {
        let output = []

        let count = 0
        while (true) {
            const y = min + count

            if (data[y]) { // if studies for current year exist
                output.push({year: y.toString(), value: data[y]})
            }
            else { // have to input 0 data
                output.push({year: y.toString(), value: 0})
            }


            count++
            if (y === maxYear) {
                break
            }
        }

        setHistogramData(output)
    }, [data, min, maxYear])

  const barGap = 1;
  const barWidth = 550/histogramData.length - barGap
  
    const svgHeight = height;
    const maxValue = Math.max(1, ...histogramData.map(d => d.value));

    return (
        <div className="mainHistogram">
            {/* <ResponsiveContainer className="histogram" width="100%" height="80%">
                
                <BarChart data={histogramData} >    
                    <Tooltip cursor={{ fill: 'transparent' }} />
                    <Bar dataKey="uv"/>
                </BarChart>
            </ResponsiveContainer> */}
            <svg
            className="histogram"
            viewBox={`0 0 550 ${svgHeight}`}
            style={{ background: "transparent", overflow: "visible" }}
            >
                {histogramData.map((d, i) => {
                const yearNum = Number(d.year)
                const inRange = yearNum >= selectedTimes[0] && yearNum <= selectedTimes[1];
                const barHeight = d.value/maxValue * svgHeight + svgHeight/40
                    return (
                     <rect
                            key={d.year}
                            x={i * (barWidth + barGap)}
                            y={svgHeight - barHeight}
                            width={barWidth}
                            height={barHeight}
                            fill={inRange ? "#0077b6" : "#aaaaaaff"} // initial color, will update via effect
                        />
                    );
                })}
            </svg>
            <div className="slideelem">
            <Slider 
                onAfterChange={applyTimes}
                className="histogramSlider"
                max={max} 
                min={min} 
                range={true}
                style={{width: '100%'}}
                tooltip={{
                    placement: "bottom",
                    open: true
                }}
                value={selectedTimes}
                onChange={(val) => {
                    if (Array.isArray(val)) {
                        setSelectedTimes([Number(val[0]), Number(val[1])])
                    }
                }}
                />
            </div>
        </div>
    ) 
    
}

export default Clinical;